/**
 * Between-play flow (owner: rules team): line-ups, next down, offside replays, conversions, possessions, overtime
 * and game over (ARCHITECTURE §5 "Football rules"). Called by `rulesPre` when the DEAD phase ends and by the
 * conversion choice. Allocation-free except for events.
 *
 * Phase-frame convention: every transition sets `rules.phaseFrame = 0`; each following `rulesPre` (and each
 * hit-stop tick) adds 1, so `phaseFrame` = ticks spent in the phase after the transition tick.
 */
import {
  DRIVE_START_YARDS_TO_GOAL,
  KICKOFF_YARDS_TO_GOAL,
  MatchFormat,
  OVERTIME_START_YARDS_TO_GOAL,
  PAT_TOSS_YARDS_TO_GOAL,
  RULES,
  SAFETY_KICK_YARDS_TO_GOAL,
  TOUCHBACK_YARDS_TO_GOAL,
  TWO_POINT_YARDS_TO_GOAL,
  xFromYardsToGoal,
  yardsToGoal,
} from '@hf/shared';
import { AfterDead, BtnIndex, ConversionChoice, Phase, PlayResultKind } from '../enums';
import { consumePress } from '../player/input';
import type { SimEventSink } from '../sink';
import { attackDirOf, lineUpForSnap, setLineToGain, startDrive, startKickoff, type SimState } from '../state';
import {
  classicRegulation,
  decidedWinner,
  otFirstPossession,
  remainingPossessions,
  spotLos,
} from './helpers';

const rem = [0, 0];

/** Score leader (0/1), or -1 when tied. */
export const leaderOf = (s: SimState): number => {
  const a = s.rules.score[0] ?? 0;
  const b = s.rules.score[1] ?? 0;
  return a > b ? 0 : b > a ? 1 : -1;
};

/** Reset the per-play rules bookkeeping after a line-up (or at the snap). */
export const resetPlayBookkeeping = (s: SimState): void => {
  const r = s.rules;
  r.completion = false;
  r.catchX = r.los;
  r.yardPower[0] = 0;
  r.yardPower[1] = 0;
  r.prevBallState = s.ball.state;
  r.prevTouchedMask = s.ball.touchedMask;
};

/** Drop the offense's stale Action / ▲ presses so a press made during the dead ball never snaps or chooses. */
const dropStalePresses = (s: SimState): void => {
  const h = s.players[s.rules.offense as 0 | 1].input;
  consumePress(h, BtnIndex.A);
  consumePress(h, BtnIndex.U);
};

const afterLineUp = (s: SimState): void => {
  resetPlayBookkeeping(s);
  dropStalePresses(s);
};

export const emitDownStart = (s: SimState, ev: SimEventSink): void => {
  if (!ev) return;
  const r = s.rules;
  ev.push({
    type: 'DOWN_START',
    tick: s.tick,
    actor: r.offense,
    down: r.down,
    distance: r.distance,
    los: r.los,
    goalToGo: r.goalToGo,
  });
};

export const emitDriveStart = (s: SimState, ev: SimEventSink): void => {
  if (!ev) return;
  const r = s.rules;
  ev.push({
    type: 'DRIVE_START',
    tick: s.tick,
    actor: r.offense,
    driveNumber: r.drivesStarted[r.offense] ?? 0,
    los: r.los,
    overtime: r.overtime,
  });
};

/**
 * Start a possession for `offense` `ytg` yards out (attack direction from `attackDirOf`: Classic swaps the ends at
 * half-time). The game clock is stopped until the snap.
 */
export const beginPossession = (s: SimState, ev: SimEventSink, offense: number, ytg: number): void => {
  startDrive(s, offense, ytg);
  s.rules.clockRunning = false;
  afterLineUp(s);
  emitDriveStart(s, ev);
  emitDownStart(s, ev);
};

/**
 * Classic: possession for `offense` at the dead-ball spot `x` (never inside RULES.minLosYardsToGoal of the goal he
 * attacks; a spot in his OWN end zone is a touchback to his 20).
 */
const beginPossessionAt = (s: SimState, ev: SimEventSink, offense: number, x: number): void => {
  const dir = attackDirOf(s, offense);
  const ytg = yardsToGoal(spotLos(x, dir), dir);
  beginPossession(s, ev, offense, ytg >= 100 ? TOUCHBACK_YARDS_TO_GOAL : ytg);
};

export const emitKickoffStart = (s: SimState, ev: SimEventSink): void => {
  if (!ev) return;
  const r = s.rules;
  ev.push({
    type: 'KICKOFF_START',
    tick: s.tick,
    actor: r.offense,
    los: r.los,
    half: r.half,
    safetyKick: yardsToGoal(r.los, r.attackDir) === SAFETY_KICK_YARDS_TO_GOAL,
  });
};

/** Classic: line up a kickoff by `kicker` (KICKOFF_START). */
export const beginKickoff = (s: SimState, ev: SimEventSink, kicker: number, ytg: number): void => {
  startKickoff(s, kicker, ytg);
  s.rules.clockRunning = false;
  afterLineUp(s);
  emitKickoffStart(s, ev);
};

export const endGame = (s: SimState, ev: SimEventSink, winner: number, tiebreak = false): void => {
  const r = s.rules;
  r.winner = winner;
  r.phase = Phase.GAME_OVER;
  r.phaseFrame = 0;
  r.rushCount = 0;
  if (ev) {
    ev.push({
      type: 'GAME_OVER',
      tick: s.tick,
      actor: winner,
      score: [r.score[0] ?? 0, r.score[1] ?? 0],
      ...(tiebreak ? { tiebreak: 'yards' as const } : null),
    });
  }
};

const startOvertimeRound = (s: SimState, ev: SimEventSink, round: number): void => {
  const r = s.rules;
  r.overtime = true;
  r.otRound = round;
  if (ev) {
    ev.push({
      type: 'OVERTIME_START',
      tick: s.tick,
      actor: -1,
      round,
      ...(round >= RULES.maxOvertimeRounds ? { lastRound: true } : null),
    });
  }
  beginPossession(s, ev, otFirstPossession(s, round), OVERTIME_START_YARDS_TO_GOAL);
};

/** Classic format: the half's clock has run out (regulation only). */
export const classicClockExpired = (s: SimState): boolean =>
  s.rules.format === MatchFormat.CLASSIC && !s.rules.overtime && s.rules.gameClock <= 0;

/**
 * Classic: the half is over (HALF_END). Half 1 → the half-time pause (DEAD, AfterDead.SECOND_HALF); half 2 → the
 * leader wins, a tie goes to overtime (college-style rounds from the 25, like Drives).
 */
const emitHalfEnd = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  if (ev) {
    ev.push({
      type: 'HALF_END',
      tick: s.tick,
      actor: -1,
      half: r.half,
      score: [r.score[0] ?? 0, r.score[1] ?? 0],
    });
  }
};

export const endHalf = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  r.clockRunning = false;
  emitHalfEnd(s, ev);
  if (r.half === 1) {
    r.phase = Phase.DEAD;
    r.phaseFrame = 0;
    r.rushCount = 0;
    r.afterDead = AfterDead.SECOND_HALF;
    return;
  }
  const leader = leaderOf(s);
  if (leader >= 0) endGame(s, ev, leader);
  else startOvertimeRound(s, ev, 1);
};

/** Classic: the second half starts with the opening receiver kicking off (ends swapped, see attackDirOf). */
const secondHalf = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  r.half = 2;
  r.gameClock = RULES.classicHalfFrames;
  beginKickoff(s, ev, s.config.firstPossession, KICKOFF_YARDS_TO_GOAL);
};

/**
 * Classic regulation: who has the ball next, and where, from the play that ended the possession (lastResult):
 * scores → the scorer kicks off (a safety: the conceding player's free kick from his 20); a missed FG → the
 * defense at the LOS (at worst its own 20); turnover on downs / interception / kick results → at the spot.
 */
const classicNext = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const lr = r.lastResult;
  const off = r.offense;
  const def = 1 - off;
  switch (lr.kind) {
    case PlayResultKind.TOUCHDOWN:
    case PlayResultKind.PICK_SIX:
    case PlayResultKind.FIELD_GOAL_GOOD:
    case PlayResultKind.CONVERSION_GOOD:
    case PlayResultKind.CONVERSION_FAILED:
      // the scoring side kicks (a return TD already handed the ball to the scorer for the try)
      beginKickoff(s, ev, off, KICKOFF_YARDS_TO_GOAL);
      return;
    case PlayResultKind.SAFETY:
      beginKickoff(s, ev, off, SAFETY_KICK_YARDS_TO_GOAL);
      return;
    case PlayResultKind.FIELD_GOAL_MISS: {
      const dir = attackDirOf(s, def);
      const ytg = Math.min(yardsToGoal(r.los, dir), TOUCHBACK_YARDS_TO_GOAL);
      beginPossession(s, ev, def, ytg);
      return;
    }
    case PlayResultKind.INTERCEPTION:
      beginPossessionAt(s, ev, lr.actor >= 0 ? lr.actor : def, lr.spotX);
      return;
    case PlayResultKind.KICK_RECOVERED:
      beginPossessionAt(s, ev, off, lr.spotX);
      return;
    default:
      // turnover on downs, kick returns / touchbacks / downed kicks: the other player at the spot
      beginPossessionAt(s, ev, def, lr.spotX);
  }
};

/**
 * Still tied after RULES.maxOvertimeRounds overtime rounds (two stonewalling — or idle — players can never loop
 * sudden death forever): the player with more yards from scrimmage over the match wins (statYards; then more
 * tackles + interceptions; then the player who did not get the opening possession).
 */
export const overtimeTiebreak = (s: SimState): number => {
  const a = s.players[0];
  const b = s.players[1];
  if (a.statYards !== b.statYards) return a.statYards > b.statYards ? 0 : 1;
  const da = a.statTackles + a.statInterceptions;
  const db = b.statTackles + b.statInterceptions;
  if (da !== db) return da > db ? 0 : 1;
  return 1 - s.config.firstPossession;
};

/** A possession is over: next possession, overtime round, or game over. */
export const afterPossession = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const next = 1 - r.offense;
  if (r.overtime) {
    const first = otFirstPossession(s, r.otRound);
    if (r.offense === first) {
      const w =
        RULES.endWhenDecided && remainingPossessions(s, rem)
          ? decidedWinner(r.score[0] ?? 0, r.score[1] ?? 0, rem[0]!, rem[1]!)
          : -1;
      if (w >= 0) endGame(s, ev, w);
      else beginPossession(s, ev, next, OVERTIME_START_YARDS_TO_GOAL);
      return;
    }
    const leader = leaderOf(s);
    if (leader >= 0) endGame(s, ev, leader);
    else if (r.otRound >= RULES.maxOvertimeRounds) endGame(s, ev, overtimeTiebreak(s), true);
    else startOvertimeRound(s, ev, r.otRound + 1);
    return;
  }
  if (classicRegulation(s)) {
    if (r.gameClock <= 0) endHalf(s, ev);
    else classicNext(s, ev);
    return;
  }
  // Drives regulation.
  remainingPossessions(s, rem);
  if (rem[0] === 0 && rem[1] === 0) {
    const leader = leaderOf(s);
    if (leader >= 0) endGame(s, ev, leader);
    else startOvertimeRound(s, ev, 1);
    return;
  }
  const w = RULES.endWhenDecided ? decidedWinner(r.score[0] ?? 0, r.score[1] ?? 0, rem[0]!, rem[1]!) : -1;
  if (w >= 0) endGame(s, ev, w);
  else beginPossession(s, ev, next, DRIVE_START_YARDS_TO_GOAL);
};

/**
 * After an offense TD (score already added): can the conversion still change who wins? False when the match is
 * decided whatever the try yields (walk-off), so the conversion is skipped and the game ends.
 */
export const conversionCanMatter = (s: SimState): boolean => {
  if (!RULES.endWhenDecided) return true;
  if (!remainingPossessions(s, rem)) return true;
  const r = s.rules;
  const off = r.offense;
  let first = -2;
  for (let c = 0; c <= 2; c++) {
    const s0 = (r.score[0] ?? 0) + (off === 0 ? c : 0);
    const s1 = (r.score[1] ?? 0) + (off === 1 ? c : 0);
    const w = decidedWinner(s0, s1, rem[0]!, rem[1]!);
    if (w < 0) return true;
    if (first === -2) first = w;
    else if (w !== first) return true;
  }
  return false;
};

/** Next down after a play that did not end the drive (spot / first down from lastResult). */
const nextDown = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const lr = r.lastResult;
  r.los = spotLos(lr.spotX, r.attackDir);
  if (lr.firstDown) {
    r.down = 1;
    setLineToGain(s);
  } else {
    r.down++;
    r.distance = Math.abs(r.firstDownX - r.los);
  }
  lineUpForSnap(s);
  afterLineUp(s);
  emitDownStart(s, ev);
};

/** Offside: replay the down from the penalty spot (automatic first down if earned; conversions keep the try). */
const replayDown = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  const lr = r.lastResult;
  r.los = spotLos(lr.spotX, r.attackDir);
  if (r.conversionPlay) {
    setLineToGain(s);
  } else if (lr.firstDown) {
    r.down = 1;
    setLineToGain(s);
  } else {
    r.distance = Math.abs(r.firstDownX - r.los);
  }
  lineUpForSnap(s);
  afterLineUp(s);
  emitDownStart(s, ev);
};

/** TD scored: the scorer chooses 1-pt toss (Action) or 2-pt play (▲); players line up at the PAT spot meanwhile. */
const enterConversion = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  r.conversion = ConversionChoice.PENDING;
  r.conversionPlay = false;
  r.down = 1;
  r.los = xFromYardsToGoal(PAT_TOSS_YARDS_TO_GOAL, r.attackDir);
  setLineToGain(s);
  lineUpForSnap(s);
  r.phase = Phase.CONVERSION;
  r.phaseFrame = 0;
  afterLineUp(s);
  if (ev) ev.push({ type: 'CONVERSION_START', tick: s.tick, actor: r.offense });
};

/** Lock in the conversion choice and line up the try. */
export const chooseConversion = (s: SimState, ev: SimEventSink, choice: ConversionChoice): void => {
  const r = s.rules;
  const toss = choice === ConversionChoice.TOSS;
  r.conversion = toss ? ConversionChoice.TOSS : ConversionChoice.TWO_POINT;
  r.conversionPlay = true;
  r.down = 1;
  r.los = xFromYardsToGoal(toss ? PAT_TOSS_YARDS_TO_GOAL : TWO_POINT_YARDS_TO_GOAL, r.attackDir);
  setLineToGain(s);
  lineUpForSnap(s);
  afterLineUp(s);
  if (ev) {
    ev.push({
      type: 'CONVERSION_CHOICE',
      tick: s.tick,
      actor: r.offense,
      choice: toss ? 'toss' : 'two_point',
    });
  }
  emitDownStart(s, ev);
};

/** Dead-ball time for the last result: longer after scores; the Classic half-time pause. */
export const deadFramesFor = (s: SimState): number =>
  s.rules.afterDead === AfterDead.SECOND_HALF
    ? RULES.halftimeFrames
    : s.rules.lastResult.points > 0
      ? RULES.scoreDeadFrames
      : RULES.deadFrames;

/** The DEAD phase is over: run `rules.afterDead`. */
export const leaveDeadBall = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  switch (r.afterDead) {
    case AfterDead.NEXT_DOWN:
      if (classicClockExpired(s)) endHalf(s, ev);
      else nextDown(s, ev);
      return;
    case AfterDead.REPLAY_DOWN:
      // A half never ends on a defensive penalty: the replayed down is untimed.
      replayDown(s, ev);
      return;
    case AfterDead.SECOND_HALF:
      secondHalf(s, ev);
      return;
    case AfterDead.CONVERSION:
      enterConversion(s, ev);
      return;
    case AfterDead.GAME_OVER:
      // (Classic: a walk-off touchdown on the final play skipped its try — the second half ends here)
      if (classicRegulation(s)) emitHalfEnd(s, ev);
      endGame(s, ev, leaderOf(s));
      return;
    default:
      afterPossession(s, ev);
  }
};
